import { useState } from "react";
import { Modal, Button, Upload, Alert, List, Typography, Tag, message } from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import useAxios from "../../hooks/useAxios";

const { Dragger } = Upload;
const { Text } = Typography;

// Internal/boilerplate fields every section's mapped row carries that
// aren't useful to show in a human-readable preview summary.
const HIDDEN_PREVIEW_KEYS = new Set([
  "id",
  "employee_id",
  "created_at",
  "updated_at",
]);

// Compact, section-agnostic one-line summary of a mapped row's values —
// this component has no per-section knowledge of which fields matter most,
// so it just shows everything that isn't boilerplate.
const summarizeRowData = (data) => {
  if (!data) return "";
  return Object.entries(data)
    .filter(([key, value]) => !HIDDEN_PREVIEW_KEYS.has(key) && value !== null && value !== "")
    .map(([, value]) => (typeof value === "boolean" ? (value ? "Yes" : "No") : value))
    .join(" · ");
};

const PREVIEW_STATUS_META = {
  will_import: { color: "success", label: "Will import" },
  duplicate: { color: "default", label: "Duplicate — will skip" },
  invalid: { color: "error", label: "Invalid" },
};

// Reusable CSV/XLSX bulk-import modal for a single FSR section, modeled on
// the working EBulletin import modal (EventsAdvisoriesPanel.js). One
// component serves every section — what changes per section is just the
// endpoint, the sample file link, and the required-columns guide text.
//
// Flow: pick a file -> "Preview" posts with preview=1 and shows a row-by-row
// breakdown of what would happen, nothing is written yet -> "Confirm Import"
// posts again for real -> existing imported/skipped/failed results view.
const FsrImportModal = ({
  open,
  onClose,
  title,
  importUrl,
  sampleFileUrl,
  sampleFileName,
  columnsGuide = [],
  onImported,
}) => {
  const { fetchData } = useAxios();
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const resetAll = () => {
    setImportFile(null);
    setPreviewResult(null);
    setImportResult(null);
  };

  const handleClose = () => {
    resetAll();
    onClose?.();
  };

  const handleChangeFile = () => {
    setImportFile(null);
    setPreviewResult(null);
  };

  const runRequest = async (preview) => {
    const formData = new FormData();
    formData.append("file", importFile);
    if (preview) {
      formData.append("preview", "1");
    }

    setImporting(true);
    try {
      const res = await fetchData({
        url: importUrl,
        method: "POST",
        data: formData,
      });
      const data = res?.data ?? res;
      if (preview) {
        setPreviewResult(data);
      } else {
        setImportResult(data);
        // Refresh the underlying section table on every successful commit,
        // not just when imported > 0 — e.g. a re-run that turns out to be
        // all duplicates should still resync with the server's current
        // state instead of leaving the page's local copy untouched.
        onImported?.(data);
      }
    } catch (e) {
      const errMsg =
        e?.response?.data?.message || "Something went wrong. Please try again.";
      message.error(errMsg);
    } finally {
      setImporting(false);
    }
  };

  const handlePrimaryAction = () => {
    if (!importFile) {
      message.warning("Please select a file first.");
      return;
    }
    runRequest(!previewResult);
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleClose}
      width={580}
      destroyOnClose
      footer={
        importResult ? (
          <Button type="primary" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            {previewResult && (
              <Button onClick={handleChangeFile}>Change File</Button>
            )}
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handlePrimaryAction}
              loading={importing}
              disabled={!importFile}
            >
              {previewResult ? "Confirm Import" : "Preview"}
            </Button>
          </>
        )
      }
    >
      {importResult ? (
        <div>
          <div style={{ marginBottom: 12 }}>
            {importResult.imported > 0 && (
              <Alert
                type="success"
                icon={<CheckCircleOutlined />}
                message={`${importResult.imported} ${importResult.imported === 1 ? "row" : "rows"} imported successfully.`}
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
            {importResult.failed > 0 && (
              <Alert
                type="warning"
                icon={<CloseCircleOutlined />}
                message={`${importResult.failed} ${importResult.failed === 1 ? "row" : "rows"} could not be imported.`}
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
            {importResult.skipped > 0 && (
              <Alert
                type="info"
                icon={<InfoCircleOutlined />}
                message={`${importResult.skipped} duplicate ${importResult.skipped === 1 ? "row" : "rows"} skipped (already in your profile).`}
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
            {importResult.imported === 0 &&
              importResult.failed === 0 &&
              !importResult.skipped && (
                <Alert
                  type="info"
                  message="No data rows were found in the file."
                  showIcon
                />
              )}
          </div>

          {importResult.errors && importResult.errors.length > 0 && (
            <div>
              <Text strong style={{ fontSize: 13 }}>
                Row errors:
              </Text>
              <List
                size="small"
                dataSource={importResult.errors}
                style={{ marginTop: 8, maxHeight: 260, overflowY: "auto" }}
                renderItem={(err) => (
                  <List.Item>
                    <div>
                      <Text strong style={{ fontSize: 13 }}>
                        Row {err.row}:
                      </Text>
                      <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                        {err.errors.map((e, i) => (
                          <li key={i}>
                            <Text type="danger" style={{ fontSize: 12 }}>
                              {e}
                            </Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </List.Item>
                )}
              />
            </div>
          )}
        </div>
      ) : previewResult ? (
        <div>
          <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 12 }}>
            Nothing has been saved yet — review what this file will do, then
            click Confirm Import to proceed, or Change File to pick a
            different one.
          </Text>
          <div style={{ marginBottom: 12 }}>
            <Alert
              type="info"
              message={`${previewResult.imported} to import, ${previewResult.skipped} duplicate, ${previewResult.failed} invalid.`}
              showIcon
            />
          </div>
          <List
            size="small"
            bordered
            dataSource={previewResult.rows || []}
            style={{ maxHeight: 320, overflowY: "auto" }}
            renderItem={(row) => {
              const meta = PREVIEW_STATUS_META[row.status] || {};
              return (
                <List.Item>
                  <div style={{ width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <Text strong style={{ fontSize: 12 }}>
                        Row {row.row}
                      </Text>
                      <Tag color={meta.color} style={{ margin: 0, fontSize: 11 }}>
                        {meta.label}
                      </Tag>
                    </div>
                    {row.status === "invalid" ? (
                      <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                        {row.errors.map((e, i) => (
                          <li key={i}>
                            <Text type="danger" style={{ fontSize: 12 }}>
                              {e}
                            </Text>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {summarizeRowData(row.data)}
                      </Text>
                    )}
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      ) : (
        <>
          {sampleFileUrl && (
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Not sure about the format?&nbsp;
              </Text>
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                href={sampleFileUrl}
                download={sampleFileName}
                style={{ padding: 0, fontSize: 13 }}
              >
                Download sample CSV
              </Button>
            </div>
          )}

          {columnsGuide.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 13 }}>
                Columns:
              </Text>
              <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                {columnsGuide.map((col) => (
                  <li key={col.name}>
                    <Text code>{col.name}</Text>
                    {col.note ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {" "}
                        — {col.note}
                      </Text>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Dragger
            accept=".csv,.xlsx,.xls"
            beforeUpload={(file) => {
              setImportFile(file);
              return false;
            }}
            onRemove={() => setImportFile(null)}
            maxCount={1}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: "#862633" }} />
            </p>
            <p className="ant-upload-text">
              Click or drag a <strong>.csv</strong> or <strong>.xlsx</strong>{" "}
              file here
            </p>
            <p className="ant-upload-hint">Max file size: 5 MB</p>
          </Dragger>
        </>
      )}
    </Modal>
  );
};

export default FsrImportModal;
